// Story library — the full-catalog "Browse all" grid, reached from the
// Read tab. P-LIBRARY-READER-FIX (audit finding #1): tapping an earned
// book now opens the real reader at /play/story?slug= (previously this
// dead-ended on the chapter map via /play/map → /play/journey, dropping
// the slug — the headline reward was un-readable). The `mastery` flag
// rides along as an optimistic gold-rim hint when the slug is a Special
// Edition. Cover-only/locked books remain non-tappable in this grid.
//
// P-MILESTONE-DERIVATION (PART F) — the day-end "story unlocked" reward layer
// surfaces here. Stories the kid EARNED via a 'story_unlocked' milestone
// event are unlocked on the shelf (one of the three MA-174 isStoryTappable
// sources — earned ∪ STARTER_SLUGS ∪ completedStories-grandfather), and the
// ones earned on a MASTERY day carry a gold "Special Edition" badge. v1
// ships the SIGNAL only — no bonus content is gated on it. The fetch +
// derivation lives in this TS route so the screen stays a pure prop-driven
// view (earnedSlugs / specialEditionSlugs are plain arrays).
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { lazy, useCallback, useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAppState, useEngineChildId } from "../App/AppStateContext.tsx";
import { mergeEarnedShelfEvents } from "../lib/engine/localMilestoneStore";
import {
  buildBookImpressionEvent,
  fetchMilestoneEventsForChild,
  logMilestoneEvent,
} from "../lib/engine/milestoneLogger";
import {
  earnedSlugsFromEvents,
  specialEditionSlugsFromEvents,
  earnedStoriesFromEvents,
} from "../lib/engine/storyPickerState";
import type { TileImpressionArgs } from "../screens/StoryLibraryScreen";

// U1 (H-A6): the Browse surface is now TSX-on-tokens (the legacy
// inline-style .jsx shelf was replaced wholesale).
const StoryLibraryScreen = lazy(
  () => import("../screens/StoryLibraryScreen"),
);

function LibraryRoute() {
  const s = useAppState();
  const navigate = useNavigate();
  // Canonical cloud students.id — keys the story_unlocked events exactly like
  // every other engine read (the trail, mastery). Null until the kid is
  // linked → no earned stories surface (the mastery baseline still applies).
  const childId = useEngineChildId();

  // MA-256 book-impression-remaining-surfaces — write identity for the
  // "next-picks" impression writes, frozen at mount: the SAME pattern
  // ReadTab's favorite-write identity and DayCompleteCelebration's
  // impression-write identity already use, so a mid-visit active-kid swap
  // can't bleed an impression write onto the wrong child.
  const localChildIdRef = useRef<string | null>(s.activeChildId ?? null);
  const cloudIdPromiseRef = useRef<Promise<string | null> | null>(null);
  useEffect(() => {
    cloudIdPromiseRef.current = s.ensureActiveStudentId?.() ?? Promise.resolve(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally mount-only: freeze at screen mount

  // Fires cloud-only (no appendLocalMilestone) — mirrors the unlock-choice
  // slice's decision: nothing kid-facing reads book_impression locally
  // today, so a local mirror write would be pure unused risk. Best-effort:
  // a missed impression never blocks or degrades the shelf.
  const handleNextPicksTileImpression = useCallback(
    (args: TileImpressionArgs) => {
      void (async () => {
        const localId = localChildIdRef.current;
        const writeId = cloudIdPromiseRef.current
          ? await cloudIdPromiseRef.current
          : s.ensureActiveStudentId
            ? await s.ensureActiveStudentId()
            : childId;
        const resolvedChildId = writeId ?? localId;
        if (!resolvedChildId) return; // no identity to attach the event to — skip
        const runId =
          typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `impression_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        const event = buildBookImpressionEvent({
          childId: resolvedChildId,
          slug: args.slug,
          shelfSurface: "next-picks",
          position: args.position,
          lockState: args.locked ? "locked" : "unlocked",
          runId: `${runId}:${args.slug}`,
        });
        void logMilestoneEvent(event);
      })();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [childId],
  );

  // All of the kid's milestone events (not today-scoped — the shelf is the
  // permanent proud-stack, so earned stories persist across days). limit 500
  // is generous headroom: story unlocks accrue ~1/day, so this covers >1 year.
  // MA-346 — merged with the local mirror (mergeEarnedShelfEvents) so an
  // offline/pre-link earn doesn't render this book as still-locked here.
  // `activeChildId` is part of the queryKey (StoryReader.tsx shares this
  // literal key on purpose — see its own comment — so both must key
  // identically or one screen's fetch would silently overwrite the other's
  // cached result under the same key).
  const { data: events = [] } = useQuery({
    enabled: !!childId || !!s.activeChildId,
    queryKey: ["library-story-unlocks", childId, s.activeChildId],
    // MA-176 — type-filtered: this query only feeds earnedSlugsFromEvents /
    // earnedStoriesFromEvents / specialEditionSlugsFromEvents (all
    // story_unlocked-only), so narrowing protects the fixed 500-row window
    // from being crowded out by growing story_read volume.
    queryFn: async () => {
      const cloud = childId
        ? await fetchMilestoneEventsForChild(childId, undefined, 500, ["story_unlocked"])
        : [];
      return mergeEarnedShelfEvents(cloud, s.activeChildId);
    },
    staleTime: 0,
  });
  const earnedSlugs = useMemo(
    () => [...earnedSlugsFromEvents(events)],
    [events],
  );
  // Newest-first earned order (mastery-aware) for the Browse-grid float-up.
  // earnedSlugsFromEvents is a Set (membership, unordered); the grid needs
  // unlock chronology, so derive the ordered slug list here.
  const earnedSlugsOrdered = useMemo(
    () => earnedStoriesFromEvents(events).map((e) => e.slug),
    [events],
  );
  const specialEditionSlugs = useMemo(
    () => [...specialEditionSlugsFromEvents(events)],
    [events],
  );

  return (
    <StoryLibraryScreen
      progress={s.progress}
      earnedSlugs={earnedSlugs}
      earnedSlugsOrdered={earnedSlugsOrdered}
      specialEditionSlugs={specialEditionSlugs}
      onNextPicksTileImpression={handleNextPicksTileImpression}
      onBack={() => navigate({ to: "/read" })}
      onOpenStory={(slug: string) => {
        // Kept for any other consumer of activeStorySlug; the slug is now
        // carried explicitly in the route so the reader doesn't depend on it.
        s.setActiveStorySlug(slug);
        navigate({
          to: "/play/story",
          // from: "library" → the reader returns here on finish/back.
          search: specialEditionSlugs.includes(slug)
            ? { slug, mastery: true, from: "library" }
            : { slug, from: "library" },
        });
      }}
    />
  );
}

export const Route = createFileRoute("/library")({
  component: LibraryRoute,
});
